// Decides whether a background (muted/loop/no-controls) video needs Vidstack
// or can just play as a plain <video>. HLS (.m3u8) needs Vidstack's bundled
// hls.js — mp4/webm play fine natively, which is lighter (no custom element
// to register, no JS chunk to load). A real player with UI (video-player.js)
// always stays Vidstack regardless of this check.
export function isHlsSrc(src) {
  return !!src && /\.m3u8(\?|#|$)/i.test(src);
}

// Bunny Stream videos come as an HLS playlist (playlist.m3u8) plus MP4
// fallbacks (play_240p.mp4 ... play_1080p.mp4), and editors paste either one in
// ACF — usually the 1080p MP4 or the playlist. For a card a few hundred px
// wide both are wasteful: 1080p is ~4x the bytes of 480p, and HLS pulls in
// Vidstack + hls.js (~190 KB gzip) before the first frame.
//
// Returns the MP4 URLs to try, in order: the smallest rendition whose long
// side (assuming 16:9) covers the box at the screen's density (capped at 2x),
// then the larger ones, then the smaller ones. Never above a pasted MP4's own
// rendition. null for anything that isn't Bunny.
//
// `available` is the video's actual renditions (data-renditions, printed by
// BunnyRenditions.php from its HLS playlist): Bunny only encodes the library's
// 480/720/1080 up to the source's resolution, so a small upload may only have
// 240p. When it's known, only those are tried; otherwise 480p and up are
// guessed first, with 240p as the last resort.
const BUNNY = /^(.*b-cdn\.net\/[\w-]+\/)(?:play_(\d+)p\.mp4|playlist\.m3u8)(\?.*)?$/i;
const BUNNY_GUESS = [480, 720, 1080];

export function isBunnySrc(src) {
  return !!src && BUNNY.test(src);
}

export function bunnyMp4Candidates(src, box, available) {
  const m = src ? src.match(BUNNY) : null;
  if (!m) return null;
  const [, base, pasted, query = ""] = m;
  const max = pasted ? Number(pasted) : Infinity;
  const known = String(available || "")
    .split(",")
    .map(Number)
    .filter((p) => p > 0)
    .sort((x, y) => x - y);

  // Layout size, not getBoundingClientRect: elements scaled by a transform at
  // load (categories-push hovers start at scale 0) would read as tiny. No size
  // (display:none, not laid out yet) falls back to 480p.
  // `box` can also be a size already measured in px (no layout read here).
  const size = typeof box === "number" ? box : box ? Math.max(box.offsetWidth || 0, box.offsetHeight || 0) : 0;
  const need = size ? size * Math.min(window.devicePixelRatio || 1, 2) : (480 * 16) / 9;

  let usable = (known.length ? known : BUNNY_GUESS).filter((p) => p <= max);
  if (!usable.length) usable = known.length ? [known[0]] : [pasted ? Number(pasted) : 480];
  let i = usable.findIndex((p) => (p * 16) / 9 >= need);
  if (i < 0) i = usable.length - 1;
  // Known renditions: only the chosen one. It exists, so a failure there isn't
  // a missing file but a playback problem (a browser that can't decode it, like
  // PageSpeed's), and walking down the list would just download every other
  // rendition too. Unknown: try them in order, 240p last.
  if (known.length) return [`${base}play_${usable[i]}p.mp4${query}`];
  const order = [...usable.slice(i), ...usable.slice(0, i).reverse()];
  if (!order.includes(240)) order.push(240);
  return order.map((p) => `${base}play_${p}p.mp4${query}`);
}

// Gives a plain <video> the first URL of `urls`, moving on to the next one
// each time it fails to load (404 on a missing rendition), and calls
// `onExhausted` once none worked. A later call on the same (pooled) element
// supersedes the previous one.
export function setVideoSources(video, urls, onExhausted) {
  const token = {};
  video._vsSources = token;
  let i = 0;
  const cleanup = () => {
    video.removeEventListener("error", onError);
    video.removeEventListener("loadeddata", cleanup);
  };
  const onError = () => {
    if (video._vsSources !== token) return cleanup();
    if (video.getAttribute("src") !== urls[i]) return;
    i++;
    if (i < urls.length) {
      video.src = urls[i];
      return;
    }
    cleanup();
    onExhausted?.();
  };
  video.addEventListener("error", onError);
  video.addEventListener("loadeddata", cleanup);
  video.src = urls[0];
}

// Vidstack's <media-player> fires its own hyphenated custom events; a plain
// <video> fires the standard DOM ones.
export function mediaLoadEvent(el) {
  return el.tagName === "MEDIA-PLAYER" ? "can-play" : "canplay";
}
